// Copyright (c) 2020 embyt GmbH. All rights reserved.
// Author: Roman Morawek

import { Action } from "@reduxjs/toolkit";
import {
  ChargerCommand,
  UserRole,
  Aggregation,
  TariffAssociation,
  ImportFileType,
  ChargerStatusNotificationSource,
} from "graphql/api";
import { Location } from "react-router-dom";

import {
  CloudSettings,
  UserAccessRights,
  Report,
  TrendMonitor,
  ReportsSettings,
  UserSettings,
  IUserChargingControl,
  ChargingMode,
  ChargerConnectionType,
} from "dbtypes";
import { TimePeriod, ICloudTrendMember, DashboardPanel } from "types";
import { ILogDataIo, ILogDataTrend } from "comtypes";
import { EnergySource, ICloudParameter, ILinxParameter, OutputCmd } from "parameter";
import { Site, User } from "states/sitestates";
import { IOcppChargingSession, IRfid } from "states/ocppstates";
import { IChargerStateEvent, ITrendDataRow } from "states/trendingstates";
import { ConnectorStatus, StopReason } from "model/connector";
import { Category, EmobilityViewMode, GraphViewMode, PowerViewMode } from "states/cachedstates";

export enum ActionType {
  // scheduler
  ACT_POLL_SCHEDULE_TICK = "ACT_POLL_SCHEDULE_TICK",
  ACT_AWAKE = "ACT_AWAKE",
  // app state events, generic control flow actions
  ACT_APP_LOADED = "ACT_APP_LOADED",
  ACT_LIVE_DATA_SUBSCRIBED = "ACT_LIVE_DATA_SUBSCRIBED",
  ACT_PARAMETER_LOADED = "ACT_PARAMETER_LOADED",
  ACT_INIT_DONE = "ACT_INIT_DONE",
  ACT_NEW_PERIOD = "ACT_NEW_PERIOD",
  ACT_SELECT_SITE = "ACT_SELECT_SITE",
  ACT_DISCONNECT_SITE = "ACT_DISCONNECT_SITE",
  ACT_HTTP_ERR_UNAUTHORIZED = "ACT_HTTP_ERR_UNAUTHORIZED",
  ACT_VISIBILITY_CHANGE = "ACT_VISIBILITY_CHANGE",

  // GUI interaction
  ACT_NAVIGATION = "ACT_NAVIGATION",
  ACT_NAVIGATION_COLLAPSE = "ACT_NAVIGATION_COLLAPSE",
  ACT_NAVIGATION_DROPDOWN_COLLAPSE = "ACT_NAVIGATION_DROPDOWN_COLLAPSE",
  ACT_CONFIRM_POPUP = "ACT_CONFIRM_POPUP",
  ACT_CONFIRM_CANCEL = "ACT_CONFIRM_CANCEL",
  ACT_SET_PAGE_DIRTY = "ACT_SET_PAGE_DIRTY",
  ACT_EDIT_VALUE = "ACT_EDIT_VALUE",
  ACT_EDIT_INPUT = "ACT_EDIT_INPUT",
  ACT_EDIT_OUTPUT = "ACT_EDIT_OUTPUT",
  ACT_EDIT_TRENDDATA = "ACT_EDIT_TRENDDATA",
  ACT_EDIT_CHARGER = "ACT_EDIT_CHARGER",
  ACT_EDIT_RFID = "ACT_EDIT_RFID",
  ACT_LINK_RFID = "ACT_LINK_RFID",
  ACT_FILTER_CHARGERNAME = "ACT_FILTER_CHARGERNAME",
  ACT_EDIT_TARIFF = "ACT_EDIT_TARIFF",
  ACT_NEW_GLOBAL_TARIFF = "ACT_NEW_GLOBAL_TARIFF",
  ACT_ZOOM_GRAPH = "ACT_ZOOM_GRAPH",
  ACT_SELECT_TIMESTAMP = "ACT_SELECT_TIMESTAMP",
  ACT_SELECT_TARIFFCIRCUITS = "ACT_SELECT_TARIFFCIRCUITS",
  ACT_SELECT_IOS = "ACT_SELECT_IOS",
  ACT_SELECT_MONITOR = "ACT_SELECT_MONITOR",
  ACT_SELECT_TRENDMONITOR = "ACT_SELECT_TRENDMONITOR",
  ACT_SELECT_EMOBILITY_WIDGET = "ACT_SELECT_EMOBILITY_WIDGET",
  ACT_SELECT_ENERGYSOURCE = "ACT_SELECT_ENERGYSOURCE",
  ACT_SELECT_HIERARCHY = "ACT_SELECT_HIERARCHY",
  ACT_SELECT_CATEGORY = "ACT_SELECT_CATEGORY",
  ACT_SELECT_GRAPHVIEWMODE = "ACT_SELECT_GRAPHVIEWMODE",
  ACT_SELECT_POWERVIEWMODE = "ACT_SELECT_POWERVIEWMODE",
  ACT_SELECT_DASHBOARD_WIDGET = "ACT_SELECT_DASHBOARD_WIDGET",
  ACT_MODAL_OK = "ACT_MODAL_OK",
  ACT_MODAL_CANCEL = "ACT_MODAL_CANCEL",
  ACT_MODIFY_USER = "ACT_MODIFY_USER",
  ACT_SET_FAVORITE = "ACT_SET_FAVORITE",

  // LOGIN/SINGUP ACTIONS

  // actions which switches pages

  // action to swich page (called when user is already loggend-in) [called only from LOCAL-SITE]
  ACT_GOTO_LOGIN = "ACT_GOTO_LOGIN",
  // action to swich page (called when user is logged out) [called from sign-up or pw-forgot screens]
  ACT_BACK_TO_LOGIN = "ACT_BACK_TO_LOGIN",
  // action called when switching to change Password screen [user is logged in]
  ACT_CHANGE_PASSWORD = "ACT_CHANGE_PASSWORD",
  // action called if user decides to cancel the pw-change [user is logged in]
  ACT_CANCEL_PWD_CHANGE = "ACT_CANCEL_PWD_CHANGE",
  // action gets called right after first successful login! [user is logged in already]
  ACT_FORCE_PWD_CHANGE = "ACT_FORCE_PWD_CHANGE",
  // action gets called if the user can't login [user is logged out]
  ACT_FORGOT_PASSWORD = "ACT_FORGOT_PASSWORD",
  // action to switch to SIGN-UP Page [user is logged out / or for Admins; (different) user is logged in!]
  ACT_SIGNUP = "ACT_SIGNUP",
  // action to switch to CONFIRM-SIGN-UP Page [user is logged out]
  ACT_SIGNUP_CONFIRM = "ACT_SIGNUP_CONFIRM",

  ACT_CHANGE_PERIOD = "ACT_CHANGE_PERIOD",
  ACT_EDIT_REPORT = "ACT_EDIT_REPORT",
  ACT_EDIT_TRENDMONITOR = "ACT_EDIT_TRENDMONITOR",
  ACT_SORT_TABLE = "ACT_SORT_TABLE",
  ACT_UPDATE_TREND = "ACT_UPDATE_TREND",
  ACT_UPDATE_IOTREND = "ACT_UPDATE_IOTREND",
  ACT_SELECT_CHARGING_SESSION = "ACT_SELECT_CHARGING_SESSION",
  ACT_START_TRANSACTION_POPUP = "ACT_START_TRANSACTION_POPUP",

  // no (direct) page change!!!
  ACT_SIGNUP_REQ = "ACT_SIGNUP_REQ",
  ACT_SIGNUP_RESEND_REQ = "ACT_SIGNUP_RESEND_REQ",
  ACT_SIGNUP_CONFIRM_REQ = "ACT_SIGNUP_CONFIRM_REQ",
  ACT_SIGNUP_SUCC = "ACT_SIGNUP_SUCC",

  ACT_LOGIN_REQ = "ACT_LOGIN_REQ",
  ACT_LOGIN_SUCC = "ACT_LOGIN_SUCC",
  ACT_LOGIN_FAIL = "ACT_LOGIN_FAIL",
  ACT_LOGIN_FAIL_AWS = "ACT_LOGIN_FAIL_AWS",
  ACT_LOGIN_CREATE_RFID_USER = "ACT_LOGIN_CREATE_RFID_USER",

  ACT_LOGOUT_REQ = "ACT_LOGOUT_REQ",
  ACT_LOGOUT_SUCC = "ACT_LOGOUT_SUCC",
  ACT_LOGOUT_FAIL = "ACT_LOGOUT_FAIL",

  ACT_FORGOT_PASSWORD_CODE_REQ = "ACT_FORGOT_PASSWORD_CODE_REQ",
  ACT_FORGOT_PASSWORD_CODE_SUCC = "ACT_FORGOT_PASSWORD_CODE_SUCC",

  ACT_FORGOT_PASSWORD_CHANGE_REQ = "ACT_FORGOT_PASSWORD_CHANGE_REQ",
  ACT_FORGOT_PASSWORD_CHANGE_SUCC = "ACT_FORGOT_PASSWORD_CHANGE_SUCC",

  ACT_COGNITO_FAIL = "ACT_COGNITO_FAIL",
  ACT_COGNITO_FAIL_AWS = "ACT_COGNITO_FAIL_AWS",

  ACT_CHANGE_AWSUSER_REQ = "ACT_CHANGE_AWSUSER_REQ",
  ACT_CHANGE_AWSUSER_SUCC = "ACT_CHANGE_AWSUSER_SUCC",
  ACT_CHANGE_AWSUSER_FAIL = "ACT_CHANGE_AWSUSER_FAIL",
  ACT_CHANGE_USER_REQ = "ACT_CHANGE_USER_REQ",
  ACT_CHANGE_USER_SUCC = "ACT_CHANGE_USER_SUCC",
  ACT_CHANGE_USER_FAIL = "ACT_CHANGE_USER_FAIL",
  ACT_GET_DEVICEINFO_REQ = "ACT_GET_DEVICEINFO_REQ",
  ACT_GET_DEVICEINFO_SUCC = "ACT_GET_DEVICEINFO_SUCC",
  ACT_GET_DEVICEINFO_FAIL = "ACT_GET_DEVICEINFO_FAIL",
  ACT_GET_SITES_REQ = "ACT_GET_SITES_REQ",
  ACT_GET_SITES_SUCC = "ACT_GET_SITES_SUCC",
  ACT_GET_SITES_FAIL = "ACT_GET_SITES_FAIL",
  ACT_GET_USERLIST_BASIC_REQ = "ACT_GET_USERLIST_BASIC_REQ",
  ACT_GET_USERLIST_BASIC_SUCC = "ACT_GET_USERLIST_BASIC_SUCC",
  ACT_GET_USERLIST_BASIC_FAIL = "ACT_GET_USERLIST_BASIC_FAIL",
  ACT_GET_USERLIST_EXT_REQ = "ACT_GET_USERLIST_EXT_REQ",
  ACT_GET_USERLIST_EXT_SUCC = "ACT_GET_USERLIST_EXT_SUCC",
  ACT_GET_USERLIST_EXT_FAIL = "ACT_GET_USERLIST_EXT_FAIL",
  ACT_GET_PARAMETER_REQ = "ACT_GET_PARAMETER_REQ",
  ACT_GET_PARAMETER_SUCC = "ACT_GET_PARAMETER_SUCC",
  ACT_GET_PARAMETER_FAIL = "ACT_GET_PARAMETER_FAIL",
  ACT_REGISTER_FOR_PUSH_DATA_REQ = "ACT_REGISTER_FOR_PUSH_DATA_REQ",
  ACT_REGISTER_FOR_PUSH_DATA_SUCC = "ACT_REGISTER_FOR_PUSH_DATA_SUCC",
  ACT_REGISTER_FOR_PUSH_DATA_FAIL = "ACT_REGISTER_FOR_PUSH_DATA_FAIL",
  ACT_REGISTER_FOR_PUSH_DATA_TIMEOUT = "ACT_REGISTER_FOR_PUSH_DATA_TIMEOUT",
  ACT_GET_ALLDATA_REQ = "ACT_GET_ALLDATA_REQ",
  ACT_GET_ALLDATA_SUCC = "ACT_GET_ALLDATA_SUCC",
  ACT_GET_ALLDATA_FAIL = "ACT_GET_ALLDATA_FAIL",
  ACT_GET_CONTROLLERDATA_REQ = "ACT_GET_CONTROLLERDATA_REQ",
  ACT_GET_CONTROLLERDATA_SUCC = "ACT_GET_CONTROLLERDATA_SUCC",
  ACT_GET_CONTROLLERDATA_FAIL = "ACT_GET_CONTROLLERDATA_FAIL",
  ACT_SAVE_CONTROLLERDATA_REQ = "ACT_SAVE_CONTROLLERDATA_REQ",
  ACT_SAVE_CONTROLLERDATA_SUCC = "ACT_SAVE_CONTROLLERDATA_SUCC",
  ACT_SAVE_CONTROLLERDATA_FAIL = "ACT_SAVE_CONTROLLERDATA_FAIL",
  ACT_SAVE_CHARGER_POWER_RESET = "ACT_SAVE_CHARGER_POWER_RESET",
  ACT_GET_ALARMS_REQ = "ACT_GET_ALARMS_REQ",
  ACT_GET_ALARMS_SUCC = "ACT_GET_ALARMS_SUCC",
  ACT_GET_ALARMS_FAIL = "ACT_GET_ALARMS_FAIL",
  ACT_APPEND_CLOUDALARM = "ACT_APPEND_CLOUDALARM",
  ACT_ACK_ALARM_REQ = "ACT_ACK_ALARM_REQ",
  ACT_ACK_ALARM_SUCC = "ACT_ACK_ALARM_SUCC",
  ACT_ACK_ALARM_FAIL = "ACT_ACK_ALARM_FAIL",
  ACT_GET_TRENDINFO_REQ = "ACT_GET_TRENDINFO_REQ",
  ACT_GET_TRENDINFO_SUCC = "ACT_GET_TRENDINFO_SUCC",
  ACT_GET_TRENDINFO_FAIL = "ACT_GET_TRENDINFO_FAIL",
  ACT_GET_OCPP_TRENDINFO_REQ = "ACT_GET_OCPP_TRENDINFO_REQ",
  ACT_GET_OCPP_TRENDINFO_SUCC = "ACT_GET_OCPP_TRENDINFO_SUCC",
  ACT_GET_OCPP_TRENDINFO_FAIL = "ACT_GET_OCPP_TRENDINFO_FAIL",
  ACT_GET_TREND_REQ = "ACT_GET_TREND_REQ",
  ACT_GET_CONTROLLERTREND_SUCC = "ACT_GET_CONTROLLERTREND_SUCC",
  ACT_GET_CLOUDTREND_SUCC = "ACT_GET_CLOUDTREND_SUCC",
  ACT_GET_CHARGERTREND_SUCC = "ACT_GET_CHARGERTREND_SUCC",
  ACT_APPEND_CLOUDTREND = "ACT_APPEND_CLOUDTREND",
  ACT_GET_TREND_FAIL = "ACT_GET_TREND_FAIL",
  ACT_GET_IOTREND_REQ = "ACT_GET_IOTREND_REQ",
  ACT_GET_CONTROLLERIOTREND_SUCC = "ACT_GET_CONTROLLERIOTREND_SUCC",
  ACT_GET_CLOUDIOTREND_SUCC = "ACT_GET_CLOUDIOTREND_SUCC",
  ACT_GET_IOTREND_FAIL = "ACT_GET_IOTREND_FAIL",
  ACT_GET_CLOUDIOTREND_FULL_SUCC = "ACT_GET_CLOUDIOTREND_FULL_SUCC",
  ACT_GET_CLOUDIOTREND_FULL_FAIL = "ACT_GET_CLOUDIOTREND_FULL_FAIL",
  ACT_CHANGE_INPUT_REQ = "ACT_CHANGE_INPUT_REQ",
  ACT_CHANGE_INPUT_SUCC = "ACT_CHANGE_INPUT_SUCC",
  ACT_CHANGE_INPUT_FAIL = "ACT_CHANGE_INPUT_FAIL",
  ACT_CHANGE_OUTPUT_REQ = "ACT_CHANGE_OUTPUT_REQ",
  ACT_CHANGE_OUTPUT_SUCC = "ACT_CHANGE_OUTPUT_SUCC",
  ACT_CHANGE_OUTPUT_FAIL = "ACT_CHANGE_OUTPUT_FAIL",
  ACT_CHANGE_TREND_REQ = "ACT_CHANGE_TREND_REQ",
  ACT_CHANGE_TREND_SUCC = "ACT_CHANGE_TREND_SUCC",
  ACT_CHANGE_TREND_FAIL = "ACT_CHANGE_TREND_FAIL",
  ACT_CHANGE_SITESETTINGS_REQ = "ACT_CHANGE_SITESETTINGS_REQ",
  ACT_CHANGE_SITESETTINGS_SUCC = "ACT_CHANGE_SITESETTINGS_SUCC",
  ACT_CHANGE_SITESETTINGS_FAIL = "ACT_CHANGE_SITESETTINGS_FAIL",
  ACT_SAVE_REPORT_REQ = "ACT_SAVE_REPORT_REQ",
  ACT_SAVE_REPORTS_SETTINGS_REQ = "ACT_SAVE_REPORTS_SETTINGS_REQ",
  ACT_SAVE_REPORT_SETTINGS_SUCC = "ACT_SAVE_REPORT_SETTINGS_SUCC",
  ACT_SAVE_REPORT_SETTINGS_FAIL = "ACT_SAVE_REPORT_SETTINGS_FAIL",
  ACT_CREATE_REPORT_REQ = "ACT_CREATE_REPORT_REQ",
  ACT_CREATE_REPORT_SUCC = "ACT_CREATE_REPORT_SUCC",
  ACT_CREATE_REPORT_DATA_SUCC = "ACT_CREATE_REPORT_DATA_SUCC",
  ACT_CREATE_REPORT_FAIL = "ACT_CREATE_REPORT_FAIL",
  ACT_NEW_REPORT = "ACT_NEW_REPORT",
  ACT_NEW_REPORT_DATA = "ACT_NEW_REPORT_DATA",
  ACT_SAVE_TRENDMONITOR_SETTINGS_REQ = "ACT_SAVE_TRENDMONITOR_SETTINGS_REQ",
  ACT_SAVE_TRENDMONITOR_SETTINGS_SUCC = "ACT_SAVE_TRENDMONITOR_SETTINGS_SUCC",
  ACT_SAVE_TRENDMONITOR_SETTINGS_FAIL = "ACT_SAVE_TRENDMONITOR_SETTINGS_FAIL",
  ACT_OPEN_SSH_TUNNEL_REQ = "ACT_OPEN_SSH_TUNNEL_REQ",
  ACT_OPEN_SSH_TUNNEL_SUCC = "ACT_OPEN_SSH_TUNNEL_SUCC",
  ACT_OPEN_SSH_TUNNEL_FAIL = "ACT_OPEN_SSH_TUNNEL_FAIL",
  ACT_GET_OCPP_CHARGERS_REQ = "ACT_GET_OCPP_CHARGERS_REQ",
  ACT_GET_OCPP_CHARGERS_SUCC = "ACT_GET_OCPP_CHARGERS_SUCC",
  ACT_GET_OCPP_CHARGERS_FAIL = "ACT_GET_OCPP_CHARGERS_FAIL",
  ACT_SAVE_OCPP_CHARGERS_REQ = "ACT_SAVE_OCPP_CHARGERS_REQ",
  ACT_SAVE_OCPP_CHARGERS_SUCC = "ACT_SAVE_OCPP_CHARGERS_SUCC",
  ACT_SAVE_OCPP_CHARGERS_FAIL = "ACT_SAVE_OCPP_CHARGERS_FAIL",
  ACT_GET_OCPP_RFIDS_BASIC_REQ = "ACT_GET_OCPP_RFIDS_BASIC_REQ",
  ACT_GET_OCPP_RFIDS_BASIC_SUCC = "ACT_GET_OCPP_RFIDS_BASIC_SUCC",
  ACT_GET_OCPP_RFIDS_BASIC_FAIL = "ACT_GET_OCPP_RFIDS_BASIC_FAIL",
  ACT_GET_OCPP_RFIDS_EXT_REQ = "ACT_GET_OCPP_RFIDS_EXT_REQ",
  ACT_GET_OCPP_RFIDS_EXT_SUCC = "ACT_GET_OCPP_RFIDS_EXT_SUCC",
  ACT_GET_OCPP_RFIDS_EXT_FAIL = "ACT_GET_OCPP_RFIDS_EXT_FAIL",
  ACT_GET_OCPP_REFUSED_RFIDS_REQ = "ACT_GET_OCPP_REFUSED_RFIDS_REQ",
  ACT_GET_OCPP_REFUSED_RFIDS_SUCC = "ACT_GET_OCPP_REFUSED_RFIDS_SUCC",
  ACT_GET_OCPP_REFUSED_RFIDS_FAIL = "ACT_GET_OCPP_REFUSED_RFIDS_FAIL",
  ACT_CHECK_OCPP_RFIDS_REQ = "ACT_CHECK_OCPP_RFIDS_REQ",
  ACT_CHECK_OCPP_RFIDS_SUCC = "ACT_CHECK_OCPP_RFIDS_SUCC",
  ACT_CHECK_OCPP_RFIDS_FAIL = "ACT_CHECK_OCPP_RFIDS_FAIL",
  ACT_SAVE_OCPP_RFID_REQ = "ACT_SAVE_OCPP_RFID_REQ",
  ACT_SAVE_OCPP_RFID_SUCC = "ACT_SAVE_OCPP_RFID_SUCC",
  ACT_SAVE_OCPP_RFID_FAIL = "ACT_SAVE_OCPP_RFID_FAIL",
  ACT_GET_OCPP_SITE_TARIFFS_REQ = "ACT_GET_OCPP_SITE_TARIFFS_REQ",
  ACT_GET_OCPP_SITE_TARIFFS_SUCC = "ACT_GET_OCPP_SITE_TARIFFS_SUCC",
  ACT_GET_OCPP_SITE_TARIFFS_FAIL = "ACT_GET_OCPP_SITE_TARIFFS_FAIL",
  ACT_GET_OCPP_SESSION_TARIFFS_SUCC = "ACT_GET_OCPP_SESSION_TARIFFS_SUCC",
  ACT_GET_OCPP_SESSION_TARIFFS_FAIL = "ACT_GET_OCPP_SESSION_TARIFFS_FAIL",
  ACT_SAVE_OCPP_TARIFF_REQ = "ACT_SAVE_OCPP_TARIFF_REQ",
  ACT_SAVE_OCPP_TARIFF_SUCC = "ACT_SAVE_OCPP_TARIFF_SUCC",
  ACT_SAVE_OCPP_TARIFF_FAIL = "ACT_SAVE_OCPP_TARIFF_FAIL",
  ACT_GET_OCPP_CONNECTORS_REQ = "ACT_GET_OCPP_CONNECTORS_REQ",
  ACT_GET_OCPP_CONNECTORS_SUCC = "ACT_GET_OCPP_CONNECTORS_SUCC",
  ACT_GET_OCPP_CONNECTORS_FAIL = "ACT_GET_OCPP_CONNECTORS_FAIL",
  ACT_GET_PENDING_OCPP_SESSIONS_REQ = "ACT_GET_PENDING_OCPP_SESSIONS_REQ",
  ACT_GET_PENDING_OCPP_SESSIONS_SUCC = "ACT_GET_PENDING_OCPP_SESSIONS_SUCC",
  ACT_GET_PENDING_OCPP_SESSIONS_FAIL = "ACT_GET_PENDING_OCPP_SESSIONS_FAIL",
  ACT_GET_OCPP_SESSIONS_REQ = "ACT_GET_OCPP_SESSIONS_REQ",
  ACT_GET_OCPP_SESSIONS_SUCC = "ACT_GET_OCPP_SESSIONS_SUCC",
  ACT_GET_OCPP_SESSIONS_FAIL = "ACT_GET_OCPP_SESSIONS_FAIL",
  ACT_NEW_OCPP_DATA = "ACT_NEW_OCPP_DATA",
  ACT_NEW_CHARGER_STATUS = "ACT_NEW_CHARGER_STATUS",
  ACT_NEW_CHARGING_SESSION_DATA = "ACT_NEW_CHARGING_SESSION_DATA",
  ACT_GET_CHARGING_STATES_SUCC = "ACT_GET_CHARGING_STATES_SUCC",
  ACT_GET_CHARGING_STATES_FAIL = "ACT_GET_CHARGING_STATES_FAIL",
  ACT_REBOOT_REQ = "ACT_REBOOT_REQ",
  ACT_REBOOT_SUCC = "ACT_REBOOT_SUCC",
  ACT_REBOOT_FAIL = "ACT_REBOOT_FAIL",
  ACT_TRIGGER_CHARGER_CMD_REQ = "ACT_TRIGGER_CHARGER_CMD_REQ",
  ACT_TRIGGER_CHARGER_CMD_SUCC = "ACT_TRIGGER_CHARGER_CMD_SUCC",
  ACT_TRIGGER_CHARGER_CMD_FAIL = "ACT_TRIGGER_CHARGER_CMD_FAIL",
  ACT_SAVE_CHARGER_POWER_REQ = "ACT_SAVE_CHARGER_POWER_REQ",
  ACT_SAVE_CHARGER_POWER_SUCC = "ACT_SAVE_CHARGER_POWER_SUCC",
  ACT_SAVE_CHARGER_POWER_FAIL = "ACT_SAVE_CHARGER_POWER_FAIL",
  ACT_GET_CHARGER_POWER_SUCC = "ACT_GET_CHARGER_POWER_SUCC",
  ACT_GET_CHARGER_POWER_FAIL = "ACT_GET_CHARGER_POWER_FAIL",
  ACT_PERFORM_FILE_UPLOAD_REQ = "ACT_PERFORM_FILE_UPLOAD_REQ",
  ACT_PERFORM_FILE_UPLOAD_SUCC = "ACT_PERFORM_FILE_UPLOAD_SUCC",
  ACT_PERFORM_FILE_UPLOAD_FAIL = "ACT_PERFORM_FILE_UPLOAD_FAIL",
  ACT_GET_FILEUPLOADS_REQ = "ACT_GET_FILEUPLOADS_REQ",
  ACT_GET_FILEUPLOADS_SUCC = "ACT_GET_FILEUPLOADS_SUCC",
  ACT_GET_FILEUPLOADS_FAIL = "ACT_GET_FILEUPLOADS_FAIL",
}

export function raiseAction<T>(type: ActionType, payload?: T): IAction<T | undefined> {
  return {
    type: ActionType[type], // convert type to string
    payload,
  };
}

export interface IAction<T> extends Action {
  type: string;
  payload: T;
  // index signature needed to be compatible with redux-toolkits UnknownAction
  // no idea why this is needed...
  [key: string]: any;
}

export interface IActionTypeConfirmPopup {
  title: string | undefined;
  message: string;
  action?: ActionType;
  actionPayload?: any;
  location?: Location;
}

export interface IActionTypeParameterLoad {
  parameter: ICloudParameter | ILinxParameter | undefined; // site may not have parameters yet
  tzOffset: number | undefined;
}

export interface IActionTypeEditEntry {
  name: string;
  value: string | boolean | number | Date | Record<string, unknown> | undefined | null;
}
export type IActionTypeEdit = IActionTypeEditEntry | IActionTypeEditEntry[];

export interface IActionTypeSignUp {
  username: string;
  password?: string;
  code?: string;
  realName?: string;
  company?: string;
  keepErrorState: boolean;
}

export interface IActionTypeLogin {
  username: string;
  password: string;
}

export interface IActionTypeChangeAwsUser {
  secondaryId: string;
  oldPassword: string;
  newPassword: string;
  realName?: string;
}

export interface IActionTypeForgotPassword {
  username: string;
  confirmationCode: string;
  newPassword: string;
}

export interface IActionTypeChangeUser {
  id?: number;
  email?: string;
  name?: string;
  company?: string;
  role?: UserRole;
  userSites?: number[];
  accessRights?: UserAccessRights;
  settings?: UserSettings;
  chargerControl?: IUserChargingControl;
  carName?: string;
  carBattery?: number;
}

export interface IActionTypeChangeSite {
  id: number;
  cloudSettings?: CloudSettings;
}

export interface IActionCreateReport {
  reportId: number;
  previewOnly: boolean;
}

export interface IActionTypeChangeReports {
  siteId: number;
  reportId: number | undefined; // undefined on new report creation
  report: Report | undefined; // undefined on report deletion
}

export interface IActionTypeChangeReportsSettings {
  siteId: number;
  reportsSettings: ReportsSettings | undefined; // undefined on report deletion
}

export interface IActionTypeChangeTrendMonitorSettings {
  siteId: number;
  trendMonitorId: number | undefined; // undefined on new creation
  trendMonitorSettings: TrendMonitor | undefined; // undefined on deletion
}

export interface IActionTypeLoginResult {
  userId: string;
  isRfidUser: boolean;
  realName?: string;
  email?: string;
}

export interface IActionTypeSignUpResult {
  userId?: string;
  realName?: string;
  destination?: string; // for now this is email only!
  medium?: string;
  attribute?: string;
  confirmed: boolean;
}

export type IActionTypeSitelist = Site[];
export type IActionTypeUserlist = User[];

export interface IActionControllerTrendInfo {
  startDate: number; // ms, timestamp
  itemCnt: number[]; // number of available items for each queried trend
}
export interface IActionCloudTrendInfo {
  minTrendTimestamp: number | undefined; // s, timestamp
  maxTrendTimestamp: number | undefined; // s, timestamp
  minInputTimestamp: number | undefined; // s, timestamp
  maxInputTimestamp: number | undefined; // s, timestamp
  minOutputTimestamp: number | undefined; // s, timestamp
  maxOutputTimestamp: number | undefined; // s, timestamp
}
export interface IActionOcppTrendInfo {
  minTrendTimestamp: number | undefined; // s, timestamp
  maxTrendTimestamp: number | undefined; // s, timestamp
}

export interface IActionControllerTrendData {
  index: number;
  data: ILogDataTrend[] | ILogDataIo[];
}

export interface IActionTrend {
  timestamp: number;
  tlIndex: number; // index, not id
}

export type IActionChangeTrend = IActionTrend & {
  regperiod: number;
  energy: number;
  costs?: number;
  tariff?: number;
  targetpower?: number | null;
};

export interface IActionCloudTrendDataRow {
  timestamp: number;
  siteId: number;
  chargerId: number | undefined;
  tlId: number;
  data: ITrendDataRow;
}
export type IActionCloudTrendData = {
  data: IActionCloudTrendDataRow[];
  progress: number; // 0..1
};

export interface IActionCloudIoDataRow {
  timestamp: number;
  siteId: number;
  source: "input" | "output";
  index: number; // index of the parameter item
  value: number;
  count: number | null | undefined;
}
export interface IActionCloudIoTrendData {
  aggregation: Aggregation;
  data: IActionCloudIoDataRow[];
  queryMoreData: boolean;
}

export interface IActionTypeZoom {
  identifier: "trendPeriod" | "ioPeriod";
  minX?: number;
  maxX?: number;
}

export interface IActionTypeSelectDashboardItem {
  index: number;
  selectedIds?: number[];
  hierarchyId?: number | null | undefined; // null for root level
  hierarchyUp?: boolean;
}

export interface IActionTypeSelectGuiItem {
  dashboardIndex: number;
  selection:
    | EmobilityViewMode
    | GraphViewMode
    | PowerViewMode
    | EnergySource
    | Category
    | undefined;
}

export enum ActionGraphCommand {
  BACKWARD,
  FORWARD,
  START,
  END,
  SET_PERIOD,
  SET_DATE,
}
export interface IActionTypeSetPeriod {
  identifier: "trendPeriod" | "ioPeriod" | "chargingSessionFilter";
  command: ActionGraphCommand;
  period?: TimePeriod;
  timestampStart?: number;
  timestampEnd?: number;
}

export interface IActionCommand {
  commandSeqNr: number;
}

export type IActionChangeInput = IActionCommand & {
  index: number;
  value: number;
};

export type IActionChangeOutput = IActionCommand & {
  index: number;
  cmd: OutputCmd;
};

export interface IActionAckAlarm {
  alarmTime: number;
  xaid: string;
  source: string;
  value: number;
}

export type IActionOcppCharger = {
  fk_charger: number;
  chargepoint: string;
  isActive: boolean;
  connectionType: ChargerConnectionType | undefined;
  lastMessage: number | undefined;
  // ocpp configuration data
  vendor?: string;
  model?: string;
  firmware?: string;
  serial?: string;
  numberofconnectors?: number;
  hasSmartCharging: boolean;
  // user configuration data
  name?: string;
  nominalPower?: number;
  restrictAuth: boolean;
  coordinates?: number[];
  tariffs: TariffAssociation[];
};

export type IActionOcppConnector = {
  fk_charger: number;
  connector: number;
  // configuration data
  lastChange: number;
  name: string | undefined;
  evseid: string | undefined;
  plugType: string | undefined;
  nominalPower: number | undefined;
  isDc: boolean | undefined;
  nrPhases: number | undefined;
  // ocpp data
  lastUpdate: number;
  power: number | undefined;
  current: number[];
  voltage: number[];
  sessionEnergy: number | undefined;
  energymeter: number | undefined;
  connectorStatus: ConnectorStatus | undefined;
  connectorStatusTimestamp: number | undefined;
};

export interface IActionChangeChargerSettings {
  id: number;
  deleteCharger?: boolean;
  name?: string;
  nominalPower?: number;
  restrictAuth?: boolean;
  coordinates?: number[];
  connector?: (
    | {
        name: string | undefined;
        evseid: string | undefined;
        plugType: string | undefined;
        nominalPower: number | undefined;
        isDc: boolean | undefined;
        nrPhases: number | undefined;
      }
    | undefined
  )[];
}

export type IActionTypeRfidList = IRfid[];

export interface IActionChangeRfid {
  id: number | undefined;
  rfid: string | undefined;
  name?: string;
  category?: string;
  chargerRestricted?: boolean;
  limitStart?: number;
  limitEnd?: number;
  limitEnergy?: number;

  isLoginAllowed?: boolean;
  isChargerPowerCtrlAllowed?: boolean;
  maxPower?: number;
}

export interface IActionLinkRfid {
  id: number | undefined;
  rfid: string | undefined;
  siteIds: number[] | undefined;
}

export interface IActionChangeTariff {
  id: number | undefined;
  chargerId?: number;
  rfidId?: number;
  name?: string | undefined;
  isActive?: boolean;
  priority?: number;
  costPerEnergy?: number;
  costPerPeriod?: number;
  validFrom?: number;
  validUntil?: number;
  timeStart?: number;
  timeEnd?: number;
}

export type IActionTypeChargingSessionList = IOcppChargingSession[];

export type IActionTypeChargerStateChangeList = IChargerStateEvent[];

export interface IActionNewReport {
  siteId: number | undefined;
  reportId: number | undefined;
  link: string;
  error: string | undefined;
}

export interface IActionNewReportData {
  siteId: number;
  reportId: number;
  data: [];
  error: string | undefined;
}

export interface IActionSortTable {
  table: string;
  column: string;
  isSortAscending: boolean;
}

export interface IActionNewChargerData {
  chargerId: number;
  connector: number;
  timestamp: number;
  power?: number;
  current?: number[];
  voltage?: number[];
  soc?: number;
  sessionEnergy?: number;
  energyMeter?: number;
  commandSeqNr?: number;
  commandHasError?: boolean;
  commandResult?: string;
  pingResult?: number;
}

export interface IActionNewChargerStatusNotification {
  chargerId: number;
  connector: number;
  timestamp: number;
  source: ChargerStatusNotificationSource;
  statusCode: ConnectorStatus | undefined;
  errorCode: string;
  info: string | undefined;
}

export interface IActionNewChargingSessionData {
  id: number;
  chargerId: number;
  connector: number;
  rfid: string;
  rfidId: number | undefined;
  startDate: number | undefined;
  stopDate: number | undefined;
  startMeter: number | undefined;
  stopMeter: number | undefined;
  stopReason: StopReason | undefined;
  tariffId: number | undefined;
  costs: number | undefined;
}

export interface IActionControllerData {
  serialNumber: string;
  swVersion: string;
  enlineStatus: number;
  cpuLoad: number;
  freeFlash: number;
  freeMem: number;
  temperature: number;
  onTime: number;
  modbusLoad: number;
  isCloudConnected: boolean;
  cloudLastMessage: string;
  cloudListener: number;
  hostname: string;
  dnsServer: string;
  ipAddress: string;
  ipSettings: string;
}

export interface IActionChargerCommand {
  chargerId: number;
  cmd: ChargerCommand;
  cmdArgs: any;
  timestampMs: number;
}

export interface IActionChargingPower {
  chargerId: number;
  transactionId: number | undefined;
  isActive: boolean;
  saveAsDefault: boolean;
  useAtNewSession: boolean;
  mode: ChargingMode;
  maxPower: number | undefined;
  targetEnergy: number | undefined;
  targetTimestamp: number;
}

export interface IActionSaveControllerData {
  operatorPassword?: string;
  swBranch?: string;
  hostname?: string;
  ipMode?: "dhcp" | "static";
  ipAddress?: string;
  netmask?: string;
  gateway?: string;
  dnsServer?: string;
}

export interface IActionChangeDashboardWidget {
  index: number;
  panel: DashboardPanel | undefined;
}

export interface IActionImporterFileUpload {
  fileType: ImportFileType;
  file: File;
  args?: {
    tariffcircuit?: number;
  };
}

export interface IActionTypeSelectIos {
  type: "analog" | "digital";
  inputIds: number[];
  outputIds: number[];
}
